import { ReactNode, useMemo } from 'react';
import { useIntl, IntlShape } from 'react-intl';
import Message from '@uds/message';
import TextButton, { Size } from '@uds/text-button';
import { BodyCopy } from '@uds/typography';
import { css } from 'linaria';

const INLINE_LINK_CLASS = css`
  margin-left: 4px;
  margin-right: 4px;
  padding: 0;
`;

const UNDERLINE_CLASS = css`
  text-decoration: underline;
`;

export const DESCRIPTION_MESSAGE_ID = 'cookie.modal.description';

type PrivacyPolicyDescriptionProps = {
  privacyPolicyUrl?: string;
  messageId?: string;
};

const createRichTextValues = (
  intl: IntlShape,
  privacyPolicyUrl?: string,
): Record<string, ReactNode | ((chunks: ReactNode) => ReactNode)> => {
  const messages = (intl as unknown as { messages?: Record<string, string> }).messages ?? {};
  const template = messages[DESCRIPTION_MESSAGE_ID] ?? '';

  const values: Record<string, ReactNode | ((chunks: ReactNode) => ReactNode)> = {
    p: (chunks: ReactNode) => <BodyCopy>{chunks}</BodyCopy>,
    b: (chunks: ReactNode) => <strong>{chunks}</strong>,
    em: (chunks: ReactNode) => <em>{chunks}</em>,
    u: (chunks: ReactNode) => <span className={UNDERLINE_CLASS}>{chunks}</span>,
  };

  for (let index = 1; index <= 10; index += 1) {
    const href = intl
      .formatMessage({
        id: `${DESCRIPTION_MESSAGE_ID}.link${index}.url`,
        defaultMessage: index === 1 ? privacyPolicyUrl ?? '' : '',
      })
      .trim();

    if (!href) {
      values[`link${index}`] = (chunks: ReactNode) => chunks;
      continue;
    }

    const text = intl
      .formatMessage({
        id: `${DESCRIPTION_MESSAGE_ID}.link${index}.text`,
        defaultMessage: '',
      })
      .trim();

    const makeLink = (content: ReactNode) => {
      if (content == null) {
        return null;
      }
      return (
        <TextButton
          className={INLINE_LINK_CLASS}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          size={Size.INHERIT}
        >
          {content}
        </TextButton>
      );
    };

    const hasRichTag = template.includes(`<link${index}>`);
    const hasVariable = template.includes(`{link${index}}`);

    if (hasRichTag) {
      values[`link${index}`] = (chunks: ReactNode) => {
        const content = text.length > 0 ? <Message id={`${DESCRIPTION_MESSAGE_ID}.link${index}.text`} /> : chunks;
        return makeLink(content) ?? chunks;
      };
      continue;
    }

    if (hasVariable) {
      const content = text.length > 0 ? <Message id={`${DESCRIPTION_MESSAGE_ID}.link${index}.text`} /> : text || href;
      const rendered = makeLink(content);
      values[`link${index}`] = rendered ?? content ?? href;
      continue;
    }

    values[`link${index}`] = (chunks: ReactNode) => chunks;
  }

  return values;
};

export default function PrivacyPolicyDescriptionMessage({
  privacyPolicyUrl,
  messageId = DESCRIPTION_MESSAGE_ID,
}: PrivacyPolicyDescriptionProps) {
  const intl = useIntl();

  const richTextValues = useMemo(() => createRichTextValues(intl, privacyPolicyUrl), [intl, privacyPolicyUrl]);

  return <Message id={messageId} values={richTextValues} />;
}
